import React, { useContext, useState, useMemo } from "react";
import styles from "./styles.module.css";
import withConsoleBase from "components/utils/ConsoleBase/withConsoleBase";
import { ShopContext } from "Context/ShopContext";
import { AlertMessagesContext } from "react-alert-messages";
import PrintIcon from "@mui/icons-material/Print";
import SyncIcon from "@mui/icons-material/Sync";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import Info from "components/utils/Alert/Info";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import ShopsHelper from "helpers/ShopsHelper";
import { useTranslation } from "react-i18next";
import BillSummaryReportService from "services/BillSummaryReportService";
import {
  Grid,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
} from "@mui/material";

function DetailRow({ label, value, highlight, isPositive, isNegative, subValue }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.8,
        px: 0.5,
        borderBottom: "1px solid #f1f5f9",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" sx={{ color: "#64748b", fontSize: "12.5px" }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: "right" }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: highlight ? 700 : 600,
            fontSize: "12.5px",
            color: isPositive ? "#16a34a" : isNegative ? "#dc2626" : highlight ? "#0f172a" : "#334155",
          }}
        >
          {value}
        </Typography>
        {subValue && (
          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", fontSize: "11px" }}>
            {subValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function MonthlySummaryReport() {
  const { t: tCommon } = useTranslation("common");
  const { t: tReport } = useTranslation("report");
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [initializingStock, setInitializingStock] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [reportData, setReportData] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const isStockInitialized = Boolean(reportData?.stock?.is_initialized);

  const isNextMonthDisabled = useMemo(() => {
    const now = new Date();
    return (
      selectedDate.getFullYear() > now.getFullYear() ||
      (selectedDate.getFullYear() === now.getFullYear() && selectedDate.getMonth() >= now.getMonth())
    );
  }, [selectedDate]);

  const loadMonthlyReport = async (targetDate = selectedDate, forceCreate = false) => {
    if (forceCreate) {
      setInitializingStock(true);
    } else {
      setLoadingIndicator(true);
    }
    try {
      const data = {
        date: DateTimeHelpers.convertDateToDMY(targetDate),
        month: targetDate.getMonth() + 1,
        year: targetDate.getFullYear(),
        force_create: forceCreate,
      };
      const resp = await BillSummaryReportService.getMonthlySummaryProfitReport(data);
      setReportData(resp);
      setIsGenerated(true);
      if (forceCreate) {
        postAlertMessage({ text: tReport("Stock valuation initialized successfully!"), type: "success" });
      }
    } catch (error) {
      postAlertMessage({ text: error.message, type: "failed" });
    } finally {
      setLoadingIndicator(false);
      setInitializingStock(false);
    }
  };

  const handleGenerateReport = () => {
    loadMonthlyReport(selectedDate);
  };

  const handlePrevMonth = () => {
    if (!isGenerated || !isStockInitialized || loadingIndicator || initializingStock) return;
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
    setSelectedDate(newDate);
    loadMonthlyReport(newDate);
  };

  const handleNextMonth = () => {
    if (!isGenerated || isNextMonthDisabled || loadingIndicator || initializingStock) return;
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
    setSelectedDate(newDate);
    loadMonthlyReport(newDate);
  };

  const handlePrintReport = () => {
    window.print();
  };

  const monthYearDisplay = selectedDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const currency = shop?.currency || "";

  // Sales
  const netSales = reportData?.sales?.net_sales ?? 0;
  const netSalesTax = reportData?.sales?.net_sales_tax ?? 0;
  const saleTotal = reportData?.sales?.sale_total ?? 0;
  const saleTax = reportData?.sales?.sale_tax ?? 0;
  const saleReturnTotal = reportData?.sales?.sale_return_total ?? 0;
  const saleReturnTax = reportData?.sales?.sale_return_tax ?? 0;
  const saleVoidTotal = reportData?.sales?.sale_void_total ?? 0;
  const saleVoidTax = reportData?.sales?.sale_void_tax ?? 0;
  const salesExpenseCost = reportData?.sales?.sales_expense_cost ?? 0;
  const salesExpenseTax = reportData?.sales?.sales_expense_tax ?? 0;
  const salesExpense = salesExpenseCost + salesExpenseTax;

  // Purchases
  const grossPurchases = reportData?.purchases?.gross_purchases ?? 0;
  const purchasesTax = reportData?.purchases?.purchases_tax ?? 0;
  const purchaseReturn = reportData?.purchases?.purchase_return ?? 0;
  const purchaseReturnTax = reportData?.purchases?.purchase_return_tax ?? 0;
  const netPurchases = reportData?.purchases?.net_purchases ?? 0;
  const netPurchasesTax = reportData?.purchases?.net_purchases_tax ?? 0;
  const totalPurchases = netPurchases + netPurchasesTax;

  // Stock
  const openingStock = reportData?.stock?.opening_stock_value;
  const closingStock = reportData?.stock?.closing_stock_value;
  const stockMovement =
    openingStock !== null && closingStock !== null && openingStock !== undefined && closingStock !== undefined
      ? closingStock - openingStock
      : null;

  // Expenses
  const totalExpenses = reportData?.expenses?.total_expenses ?? 0;
  const expenseItems = reportData?.expenses?.items || [];

  // Payments / Cash flows
  const custPaymentsReceived = reportData?.payments?.total_customer_payments_received ?? 0;
  const vendorPaymentsMade = reportData?.payments?.total_vendor_payments_made ?? 0;
  const custCreditUsed = reportData?.payments?.total_customer_credit_used ?? 0;
  const netCashSettlement = custPaymentsReceived - vendorPaymentsMade;

  // COGS & Profits: COGS is sales_expense, Net Profit is grossProfit - totalExpenses
  const cogs = salesExpenseCost + salesExpenseTax;
  const grossProfit = netSales - cogs + (netSalesTax - salesExpenseTax);
  const netProfit = grossProfit - totalExpenses;

  // Percentages & Margins
  const cogsPercentage = netSales > 0 ? (cogs / netSales) * 100 : 0;
  const grossProfitMargin = netSales > 0 ? (grossProfit / netSales) * 100 : 0;
  const netProfitMargin = netSales > 0 ? (netProfit / netSales) * 100 : 0;
  const expensesPercentage = netSales > 0 ? (totalExpenses / netSales) * 100 : 0;

  return (
    <div className={styles.contentWrapper}>
      {/* Print-Only Header */}
      {isGenerated && reportData && (
        <Box className={styles.printOnly} sx={{ mb: 2.5, pb: 2, borderBottom: "2px solid #334155" }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {shop?.shop_name || "SANOFT POS"}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "#475569", fontWeight: 600 }}>
            {tReport("Monthly Financial Summary Report - {{month}}", { month: monthYearDisplay })}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {tReport("Generated on: {{date}}", { date: new Date().toLocaleDateString() })}
          </Typography>
        </Box>
      )}

      {/* Header */}
      <div className={`${styles.titleSec} ${styles.noPrint}`}>
        <span className={styles.title}>
          {tCommon("Reports")}
          <span className={styles.menuTitle}>{tReport("Monthly Report")}</span>
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: "#00a65a" }}
            onClick={handlePrintReport}
            disabled={!isGenerated || !reportData || !isStockInitialized}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon("Print")}
          </Button>
        </div>
      </div>

      {/* Date Filter & Generation Section */}
      <div className={`${styles.changeable} ${styles.noPrint}`}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport("Monthly Financial Summary for {{month}}", {
                month: monthYearDisplay,
              })}
            </h2>
          </Grid>

          {/* Month Navigation Filter */}
          <Grid item xs={12} sm={8} md={5}>
            <div style={{ paddingBottom: "4px" }}>
              <label className={styles.label}>{tReport("Select Month")}</label>
            </div>
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip
                title={
                  !isGenerated
                    ? tReport("Please generate the report first")
                    : !isStockInitialized
                    ? tReport("Stock valuation must be initialized before navigating to previous months")
                    : tReport("Previous Month")
                }
              >
                <span>
                  <IconButton
                    onClick={handlePrevMonth}
                    disabled={!isGenerated || !isStockInitialized || loadingIndicator || initializingStock}
                    sx={{
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #d2d6de",
                      borderRadius: "4px",
                      width: 40,
                      height: 40,
                      "&:hover": { backgroundColor: "#e4e6eb" },
                      "&.Mui-disabled": { backgroundColor: "#f9f9f9", opacity: 0.6 },
                    }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Box
                sx={{
                  border: "1px solid #d2d6de",
                  borderRadius: "4px",
                  height: 40,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  minWidth: "180px",
                  backgroundColor: "#fff",
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#333",
                  userSelect: "none",
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 18, color: "#64748b" }} />
                <span>{monthYearDisplay}</span>
              </Box>

              <Tooltip
                title={
                  !isGenerated
                    ? tReport("Please generate the report first")
                    : isNextMonthDisabled
                    ? tReport("Future months are not selectable")
                    : tReport("Next Month")
                }
              >
                <span>
                  <IconButton
                    onClick={handleNextMonth}
                    disabled={!isGenerated || isNextMonthDisabled || loadingIndicator || initializingStock}
                    sx={{
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #d2d6de",
                      borderRadius: "4px",
                      width: 40,
                      height: 40,
                      "&:hover": { backgroundColor: "#e4e6eb" },
                      "&.Mui-disabled": { backgroundColor: "#f9f9f9", opacity: 0.6 },
                    }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>

          {/* Generate Button */}
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="contained"
              className={styles.greenBtn}
              onClick={handleGenerateReport}
              disabled={loadingIndicator || initializingStock}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {loadingIndicator ? tReport("Generating...") : tCommon("Generate Report")}
            </Button>
          </Grid>
        </Grid>
      </div>

      {/* Initial Empty State / Prompt */}
      {!reportData && (
        <Info
          title={tReport("Set filters and click generate button")}
          content={tReport(
            "Reports are generated based on the filters. Please select a month and click generate button to generate the report."
          )}
        />
      )}

      {/* Generated Report Data */}
      {reportData && (
        <div style={{ marginTop: "14px" }}>
          {/* Uninitialized State: Full Information & Confirmation View */}
          {!isStockInitialized ? (
            <Card
              sx={{
                mb: 3,
                border: "1px solid #fcd34d",
                borderRadius: "10px",
                background: "#ffffff",
                boxShadow: "0 4px 20px rgba(245, 158, 11, 0.08)",
                overflow: "hidden",
              }}
            >
              {/* Card Header Banner */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                  borderBottom: "1px solid #fde68a",
                  p: { xs: 2.5, md: 3 },
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#fef08a",
                    border: "1px solid #fcd34d",
                    borderRadius: "50%",
                    width: 52,
                    height: 52,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <WarningAmberRoundedIcon sx={{ color: "#d97706", fontSize: 32 }} />
                </Box>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={700} color="#92400e" sx={{ fontSize: "1.2rem", mb: 0.5 }}>
                    {tReport("Stock Valuation Not Initialized for {{month}}", { month: monthYearDisplay })}
                  </Typography>
                  <Typography variant="body2" color="#78350f" sx={{ lineHeight: 1.6 }}>
                    {tReport(
                      "Stock valuation baseline is required to calculate Cost of Goods Sold (COGS), Gross Profit, and Net Profit for this month. Once initialized, the full monthly financial figures and previous months navigation will be unlocked."
                    )}
                  </Typography>
                </Box>
              </Box>

              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                {/* Important Confirmation / Checklist Box */}
                <Box
                  sx={{
                    backgroundColor: "#fffdfa",
                    border: "1px solid #fed7aa",
                    borderRadius: "8px",
                    p: 2.5,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="#9a3412"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 1.5, fontSize: "15px" }}
                  >
                    <InfoOutlinedIcon sx={{ color: "#ea580c", fontSize: 20 }} />
                    {tReport("Important: Confirm Previous Month Stock Transactions Before Initializing")}
                  </Typography>
                  <Typography variant="body2" color="#475569" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {tReport(
                      "Before initializing, please ensure that all stock-affecting transactions and actions for previous months are completely synced and merged into cloud:"
                    )}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{ p: 1.5, borderRadius: "6px", backgroundColor: "#ffffff", border: "1px solid #f1f5f9" }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 20, mt: 0.2 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {tReport("Sales & Sales Returns")}
                          </Typography>
                          <Typography variant="caption" color="#64748b" display="block">
                            {tReport("All customer bills, invoices, and credit returns are merged.")}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{ p: 1.5, borderRadius: "6px", backgroundColor: "#ffffff", border: "1px solid #f1f5f9" }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 20, mt: 0.2 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {tReport("Purchases & Purchase Returns")}
                          </Typography>
                          <Typography variant="caption" color="#64748b" display="block">
                            {tReport("All vendor invoices, GRNs, and purchase returns are recorded.")}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{ p: 1.5, borderRadius: "6px", backgroundColor: "#ffffff", border: "1px solid #f1f5f9" }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 20, mt: 0.2 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {tReport("Damages & Wastage Entries")}
                          </Typography>
                          <Typography variant="caption" color="#64748b" display="block">
                            {tReport("All damaged, expired, or written-off items are logged.")}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{ p: 1.5, borderRadius: "6px", backgroundColor: "#ffffff", border: "1px solid #f1f5f9" }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 20, mt: 0.2 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {tReport("Stock Adjustments & Transfers")}
                          </Typography>
                          <Typography variant="caption" color="#64748b" display="block">
                            {tReport("All physical audit adjustments, transfers, and productions are applied.")}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Bottom Action Area */}
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  sx={{ pt: 1 }}
                >
                  <Typography variant="body2" color="#64748b">
                    {tReport("Ready to establish the baseline stock valuation snapshot for this month?")}
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={() => {
                      setConfirmChecked(false);
                      setConfirmDialogOpen(true);
                    }}
                    disabled={initializingStock || loadingIndicator}
                    sx={{
                      backgroundColor: "#d97706",
                      "&:hover": { backgroundColor: "#b45309" },
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "14px",
                      px: 3,
                      py: 1.2,
                      borderRadius: "6px",
                      boxShadow: "0 2px 6px rgba(217, 119, 6, 0.3)",
                    }}
                    startIcon={
                      initializingStock ? <CircularProgress size={18} color="inherit" /> : <Inventory2OutlinedIcon />
                    }
                  >
                    {initializingStock ? tReport("Initializing Stock Valuation...") : tReport("Initialize Stock Valuation")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            /* Initialized State: Render Executive KPIs, 4 Detailed Breakdown Cards, P&L Statement, and Expenses Table */
            <>
              {/* Executive KPI Overview Cards */}
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {/* Net Sales */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      borderTop: "4px solid #2563eb",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px" }}
                        >
                          {tReport("NET SALES")}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "#eff6ff",
                            color: "#2563eb",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />
                        </Box>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.8 }}>
                        {currency} {ShopsHelper.getAmountFormatted(shop, netSales)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.8} flexWrap="wrap">
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px" }}>
                          {tReport("Gross: {{gross}}", {
                            gross: `${currency} ${ShopsHelper.getAmountFormatted(shop, saleTotal)}`,
                          })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                          •
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px" }}>
                          {tReport("Tax: {{tax}}", {
                            tax: `${currency} ${ShopsHelper.getAmountFormatted(shop, netSalesTax)}`,
                          })}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Cost of Goods Sold (COGS) */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      borderTop: "4px solid #d97706",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px" }}
                        >
                          {tReport("COST OF GOODS SOLD (COGS)")}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "#fef3c7",
                            color: "#d97706",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ShoppingCartOutlinedIcon sx={{ fontSize: 20 }} />
                        </Box>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#334155", mb: 0.8 }}>
                        {currency} {ShopsHelper.getAmountFormatted(shop, cogs)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px" }}>
                          {tReport("Sales Item Expense")}
                        </Typography>
                        <Chip
                          label={`${cogsPercentage.toFixed(1)}%`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: "#fef3c7",
                            color: "#92400e",
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Gross Profit */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      borderTop: `4px solid ${grossProfit >= 0 ? "#16a34a" : "#dc2626"}`,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px" }}
                        >
                          {tReport("GROSS PROFIT")}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: grossProfit >= 0 ? "#dcfce7" : "#fee2e2",
                            color: grossProfit >= 0 ? "#16a34a" : "#dc2626",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {grossProfit >= 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 20 }} />
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          color: grossProfit >= 0 ? "#15803d" : "#b91c1c",
                          mb: 0.8,
                        }}
                      >
                        {currency} {ShopsHelper.getAmountFormatted(shop, grossProfit)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px" }}>
                          {tReport("Net Sales - COGS")}
                        </Typography>
                        <Chip
                          label={`${grossProfitMargin.toFixed(1)}%`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: grossProfit >= 0 ? "#dcfce7" : "#fee2e2",
                            color: grossProfit >= 0 ? "#166534" : "#991b1b",
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Net Profit / (Loss) */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: `1px solid ${netProfit >= 0 ? "#86efac" : "#fca5a5"}`,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                      background:
                        netProfit >= 0
                          ? "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)"
                          : "linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)",
                      borderTop: `4px solid ${netProfit >= 0 ? "#15803d" : "#b91c1c"}`,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px" }}
                        >
                          {tReport("NET PROFIT / (LOSS)")}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: netProfit >= 0 ? "#dcfce7" : "#fee2e2",
                            color: netProfit >= 0 ? "#15803d" : "#b91c1c",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {netProfit >= 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 20 }} />
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          color: netProfit >= 0 ? "#15803d" : "#b91c1c",
                          mb: 0.8,
                        }}
                      >
                        {currency} {ShopsHelper.getAmountFormatted(shop, netProfit)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px" }}>
                          {tReport("Gross Profit - Expenses")}
                        </Typography>
                        <Chip
                          label={`${netProfitMargin.toFixed(1)}%`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: netProfit >= 0 ? "#dcfce7" : "#fee2e2",
                            color: netProfit >= 0 ? "#166534" : "#991b1b",
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* 4 Core Operational Breakdown Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Sales Breakdown Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                      borderTop: "3px solid #10b981",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.8,
                        pb: 1.2,
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: "#fafbfc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <MonetizationOnOutlinedIcon sx={{ color: "#10b981", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "13.5px" }}>
                          {tReport("Sales Breakdown")}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 1.5, flex: 1, "&:last-child": { pb: 1.5 } }}>
                      <DetailRow
                        label={tReport("Gross Sales")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, saleTotal)}`}
                      />
                      <DetailRow
                        label={tReport("Sales Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, saleTax)}`}
                      />
                      <DetailRow
                        label={tReport("Sales Returns")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, saleReturnTotal)}`}
                        isNegative={saleReturnTotal < 0 || saleReturnTotal > 0}
                      />
                      <DetailRow
                        label={tReport("Return Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, saleReturnTax)}`}
                        isNegative={saleReturnTax < 0}
                      />
                      <DetailRow
                        label={tReport("Net Sales")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, netSales)}`}
                        highlight
                      />
                      <DetailRow
                        label={tReport("Net Sales Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, netSalesTax)}`}
                      />
                      <DetailRow
                        label={tReport("Sales Cost")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, salesExpenseCost)}`}
                      />
                      <DetailRow
                        label={tReport("Sales Cost Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, salesExpenseTax)}`}
                      />
                      <DetailRow
                        label={tReport("Cost of Sales (COGS)")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, salesExpense)}`}
                        highlight
                      />
                      {saleVoidTotal > 0 && (
                        <DetailRow
                          label={tReport("Void Sales")}
                          value={`${currency} ${ShopsHelper.getAmountFormatted(shop, saleVoidTotal)}`}
                          isNegative
                        />
                      )}
                      {saleVoidTax > 0 && (
                        <DetailRow
                          label={tReport("Void Tax")}
                          value={`${currency} ${ShopsHelper.getAmountFormatted(shop, saleVoidTax)}`}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Purchases Breakdown Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                      borderTop: "3px solid #3b82f6",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.8,
                        pb: 1.2,
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: "#fafbfc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalShippingOutlinedIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "13.5px" }}>
                          {tReport("Purchases Breakdown")}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 1.5, flex: 1, "&:last-child": { pb: 1.5 } }}>
                      <DetailRow
                        label={tReport("Gross Purchases")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, grossPurchases)}`}
                      />
                      <DetailRow
                        label={tReport("Purchases Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, purchasesTax)}`}
                      />
                      <DetailRow
                        label={tReport("Purchase Returns")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, purchaseReturn)}`}
                      />
                      <DetailRow
                        label={tReport("Return Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, purchaseReturnTax)}`}
                      />
                      <DetailRow
                        label={tReport("Net Purchases")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, netPurchases)}`}
                      />
                      <DetailRow
                        label={tReport("Net Purchases Tax")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, netPurchasesTax)}`}
                      />
                      <DetailRow
                        label={tReport("Total Purchases")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, totalPurchases)}`}
                        highlight
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Stock Valuation & Movement Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                      borderTop: "3px solid #8b5cf6",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.8,
                        pb: 1.2,
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: "#fafbfc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Inventory2OutlinedIcon sx={{ color: "#8b5cf6", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "13.5px" }}>
                          {tReport("Stock Valuation")}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 1.5, flex: 1, "&:last-child": { pb: 1.5 } }}>
                      <DetailRow
                        label={tReport("Opening Stock Value")}
                        value={
                          openingStock !== null && openingStock !== undefined
                            ? `${currency} ${ShopsHelper.getAmountFormatted(shop, openingStock)}`
                            : tReport("Not Set")
                        }
                      />
                      <DetailRow
                        label={tReport("Closing Stock Value")}
                        value={
                          closingStock !== null && closingStock !== undefined
                            ? `${currency} ${ShopsHelper.getAmountFormatted(shop, closingStock)}`
                            : tReport("Not Set")
                        }
                        highlight
                      />
                      <DetailRow
                        label={tReport("Net Stock Movement")}
                        value={
                          stockMovement !== null
                            ? `${stockMovement >= 0 ? "+" : ""}${currency} ${ShopsHelper.getAmountFormatted(shop, stockMovement)}`
                            : "-"
                        }
                        isPositive={stockMovement !== null && stockMovement > 0}
                        isNegative={stockMovement !== null && stockMovement < 0}
                      />
                      <DetailRow
                        label={tReport("Valuation Status")}
                        value={isStockInitialized ? tReport("Initialized") : tReport("Pending")}
                        isPositive={isStockInitialized}
                        isNegative={!isStockInitialized}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Cash & Credit Activity Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                      borderTop: "3px solid #f59e0b",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.8,
                        pb: 1.2,
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: "#fafbfc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalanceWalletOutlinedIcon sx={{ color: "#f59e0b", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "13.5px" }}>
                          {tReport("Cash & Settlements")}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 1.5, flex: 1, "&:last-child": { pb: 1.5 } }}>
                      <DetailRow
                        label={tReport("Customer Payments")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, custPaymentsReceived)}`}
                        isPositive={custPaymentsReceived > 0}
                      />
                      <DetailRow
                        label={tReport("Vendor Payments")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, vendorPaymentsMade)}`}
                      />
                      <DetailRow
                        label={tReport("Customer Credit Used")}
                        value={`${currency} ${ShopsHelper.getAmountFormatted(shop, custCreditUsed)}`}
                        highlight
                      />
                      <DetailRow
                        label={tReport("Net Cash Flow")}
                        value={`${netCashSettlement >= 0 ? "+" : ""}${currency} ${ShopsHelper.getAmountFormatted(shop, netCashSettlement)}`}
                        isPositive={netCashSettlement > 0}
                        isNegative={netCashSettlement < 0}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Lower Section: P&L Financial Statement & Operating Expenses */}
              <Grid container spacing={2.5}>
                {/* Monthly Profit & Loss Statement */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#fafbfc",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <AssessmentOutlinedIcon sx={{ color: "#0f172a", fontSize: 22 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                          {tReport("Profit & Loss Statement")}
                        </Typography>
                      </Box>
                      <Chip
                        label={monthYearDisplay}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: "11px", backgroundColor: "#e2e8f0", color: "#334155" }}
                      />
                    </Box>

                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {/* 1. Net Sales Revenue */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.2,
                            borderRadius: "6px",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                              (+) {tReport("Net Sales Revenue")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {tReport("Gross Sales less Returns")}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {currency} {ShopsHelper.getAmountFormatted(shop, netSales)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                              100.0%
                            </Typography>
                          </Box>
                        </Box>

                        {/* 2. Less COGS */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.2,
                            borderRadius: "6px",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#dc2626" }}>
                              (-) {tReport("Cost of Goods Sold (COGS)")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {tReport("Direct product cost of sold items")}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body1" sx={{ fontWeight: 700, color: "#dc2626" }}>
                              - {currency} {ShopsHelper.getAmountFormatted(shop, cogs)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                              {cogsPercentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>

                        <Divider />

                        {/* 3. Gross Profit */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.4,
                            borderRadius: "6px",
                            backgroundColor: grossProfit >= 0 ? "#f0fdf4" : "#fef2f2",
                            border: `1px solid ${grossProfit >= 0 ? "#bbf7d0" : "#fecaca"}`,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: grossProfit >= 0 ? "#166534" : "#991b1b" }}
                            >
                              (=) {tReport("Gross Profit")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: grossProfit >= 0 ? "#15803d" : "#b91c1c" }}>
                              {tReport("Net Sales - COGS")}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 800, color: grossProfit >= 0 ? "#15803d" : "#b91c1c" }}
                            >
                              {currency} {ShopsHelper.getAmountFormatted(shop, grossProfit)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: grossProfit >= 0 ? "#15803d" : "#b91c1c",
                              }}
                            >
                              {grossProfitMargin.toFixed(1)}% {tReport("Margin")}
                            </Typography>
                          </Box>
                        </Box>

                        {/* 4. Less Operating Expenses */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.2,
                            borderRadius: "6px",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#dc2626" }}>
                              (-) {tReport("Operating Expenses")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {tReport("Rent, utilities, salaries, and store overheads")}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body1" sx={{ fontWeight: 700, color: "#dc2626" }}>
                              - {currency} {ShopsHelper.getAmountFormatted(shop, totalExpenses)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                              {expensesPercentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>

                        <Divider />

                        {/* 5. Net Profit / (Loss) */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.5,
                            borderRadius: "8px",
                            backgroundColor: netProfit >= 0 ? "#ecfdf5" : "#fef2f2",
                            border: `2px solid ${netProfit >= 0 ? "#86efac" : "#fca5a5"}`,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 800, color: netProfit >= 0 ? "#14532d" : "#7f1d1d" }}
                            >
                              (=) {tReport("Net Profit / (Loss)")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: netProfit >= 0 ? "#166534" : "#991b1b" }}>
                              {tReport("Gross Profit - Operating Expenses")}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 800, color: netProfit >= 0 ? "#15803d" : "#b91c1c" }}
                            >
                              {currency} {ShopsHelper.getAmountFormatted(shop, netProfit)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: netProfit >= 0 ? "#15803d" : "#b91c1c",
                              }}
                            >
                              {netProfitMargin.toFixed(1)}% {tReport("Net Margin")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Operating Expenses Table */}
                <Grid item xs={12} md={6}>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#fafbfc",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {tReport("Operating Expenses")}
                      </Typography>
                      <Chip
                        label={`${expenseItems.length} ${tReport("Categories")}`}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: "11px", backgroundColor: "#f1f5f9", color: "#475569" }}
                      />
                    </Box>

                    <Table size="small" sx={{ flex: 1 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.2, fontSize: "12.5px" }}>
                            {tReport("Expense Category")}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, color: "#475569", py: 1.2, fontSize: "12.5px" }}
                          >
                            {tReport("Amount")}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, color: "#475569", py: 1.2, fontSize: "12.5px", width: "90px" }}
                          >
                            {tReport("Share")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expenseItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                              {tReport("No expenses recorded in this period")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          expenseItems.map((exp, index) => {
                            const share = totalExpenses > 0 ? ((exp.amount / totalExpenses) * 100).toFixed(1) : 0;
                            return (
                              <TableRow
                                key={exp.type_id || exp.name || index}
                                sx={{
                                  "&:hover": { backgroundColor: "#f8fafc" },
                                }}
                              >
                                <TableCell sx={{ py: 1.1, fontSize: "13px", fontWeight: 500, color: "#334155" }}>
                                  {exp.name}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.1, fontSize: "13px", fontWeight: 600, color: "#0f172a" }}
                                >
                                  {currency} {ShopsHelper.getAmountFormatted(shop, exp.amount)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.1, fontSize: "12px", color: "#64748b", fontWeight: 600 }}
                                >
                                  {share}%
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                        <TableRow sx={{ backgroundColor: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: "13.5px", color: "#0f172a", py: 1.4 }}>
                            {tReport("Total Expenses")}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, fontSize: "13.5px", color: "#0f172a", py: 1.4 }}
                          >
                            {currency} {ShopsHelper.getAmountFormatted(shop, totalExpenses)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, fontSize: "12px", color: "#475569", py: 1.4 }}
                          >
                            100.0%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </>
          )}
        </div>
      )}

      {/* Confirmation Dialog before Initializing Stock Valuation */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !initializingStock && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#1e293b", pb: 1 }}>
          {tReport("Confirm Stock Valuation Initialization")}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ color: "#334155", mb: 2, fontSize: "14px", lineHeight: 1.6 }}>
            {tReport(
              "You are about to initialize stock valuation for {{month}}. This establishes the permanent opening stock baseline for this month.",
              { month: monthYearDisplay }
            )}
          </DialogContentText>
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "6px",
              p: 2,
              mb: 2.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="#92400e" sx={{ mb: 1 }}>
              {tReport("Please confirm the following are merged & up-to-date:")}
            </Typography>
            <Typography variant="body2" color="#78350f" sx={{ fontSize: "13px", lineHeight: 1.7 }}>
              • {tReport("All previous month Sales and Customer Credit Returns")}
              <br />
              • {tReport("All previous month Purchases and Vendor Returns")}
              <br />
              • {tReport("All Damages, Wastage entries, and Stock Adjustments")}
              <br />• {tReport("All Productions and Internal Stock Transfers")}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight={600} color="#1e293b">
                {tReport("I confirm that all previous month stock-affecting transactions are merged and accurate.")}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={initializingStock}
            sx={{ textTransform: "none", color: "#64748b" }}
          >
            {tCommon("Cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!confirmChecked || initializingStock}
            onClick={async () => {
              setConfirmDialogOpen(false);
              await loadMonthlyReport(selectedDate, true);
            }}
            sx={{
              backgroundColor: "#00a65a",
              "&:hover": { backgroundColor: "#008d4c" },
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
            }}
            startIcon={initializingStock ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {initializingStock ? tReport("Initializing...") : tReport("Confirm & Initialize")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withConsoleBase(MonthlySummaryReport, {
  selectedTab: "monthly-report",
});
